import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';

import { AnalyticsEvents } from '@llura/constants';

import { useHaptic } from './useHaptic';
import { trackEvent } from '../lib/analytics';

type AudioLike = { pause: () => void };

interface UseSafeExitResult {
  /** Imperatively trigger the safe-exit flow. Pauses every registered audio */
  /** ref, fires a gentle 3-beat haptic, and routes to the calm exit screen. */
  triggerSafeExit: () => void;
  /** Register an audio element (or any object with `.pause()`) that should */
  /** stop the moment safe exit is triggered. Returns an unregister callback. */
  registerAudio: (ref: AudioLike | null) => () => void;
}

/**
 * Drives the "ritual sicher beenden" / safe-exit affordance. Phase 1 only
 * exposes the imperative trigger — callers wire it to a visible button. A
 * volume-key gesture and `<audio>` ref auto-pause are scaffolded but the
 * gesture itself is intentionally deferred (browser/PWA volume keys don't
 * reliably bubble to JS; we plan to revisit with the native shell).
 *
 * Navigation target is `/dashboard?reason=safe-exit` for now; phase 4 will
 * route to `/afterglow?reason=safe-exit` once that screen exists.
 */
export function useSafeExit(): UseSafeExitResult {
  const navigate = useNavigate();
  const haptic = useHaptic();
  const audiosRef = useRef<Set<AudioLike>>(new Set());

  const registerAudio = useCallback((ref: AudioLike | null) => {
    if (!ref) return () => {};
    audiosRef.current.add(ref);
    return () => {
      audiosRef.current.delete(ref);
    };
  }, []);

  const triggerSafeExit = useCallback(() => {
    for (const a of audiosRef.current) {
      try {
        a.pause();
      } catch {
        // ignore — best effort
      }
    }
    haptic.pulse();
    trackEvent(AnalyticsEvents.SafeExitTriggered, {});
    navigate('/afterglow?reason=safe-exit', { replace: true });
  }, [haptic, navigate]);

  return { triggerSafeExit, registerAudio };
}
